import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Button, FormGroup } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";

import { ProductTag } from "components/products/ProductPicker";
import FormField from "components/cck/FormField";
import { useConfirm } from "components/hooks/useConfirm";
import { FormDialog } from "components/dialogs/Dialogs";
import { LastShippedProductsProps } from "components/products/LastShippedProducts";

import { ProductLink } from "utils/LinkUtils";

import { Language, usePermission } from "imports/Utils";

import ProductSelect from "view/products/components/select/ProductSelect";
import { ProductTypes } from "view/products/utils/product.types";

import { Product } from "types/index";

import { gql } from "__generated__/gql";
import "../../gql/fragments";

import "./styling.scss";

const LastShippedProductsMasterData = ({
	voyage,
	ship,
	refetch,
	forceReadOnly,
}: Pick<LastShippedProductsProps, "voyage" | "ship" | "refetch" | "forceReadOnly">) => {

	const { isReadOnly, isAdmin, isCofanoAdmin } = usePermission();

	const getLastShippedProducts = () => {
		if (voyage?.lastShippedProducts && voyage.lastShippedProducts.length > 0) {
			return voyage.lastShippedProducts as Product[];
		} else if (ship?.lastShippedProducts && ship.lastShippedProducts.length > 0) {
			return ship.lastShippedProducts as Product[];
		} else {
			return [];
		}
	};

	const products = getLastShippedProducts();

	const [ dialogOpen, openDialog ] = useState(false);

	return (
		<FormGroup label={(
			<span style={{ alignItems: "center", display: "flex" }}>
				{intl.formatMessage({ id: "RECENTLY_SHIPPED_PRODUCTS" })}

				{(voyage && !forceReadOnly && !isReadOnly && (isAdmin || isCofanoAdmin)) && (
					<Button
						onClick={() => openDialog(true)}
						icon={<EditIcon fontSize="small" />}
						style={{ marginLeft: 8 }}
						variant="text"
						size="small" />
				)}
			</span>
		)}
		overflow="hidden">
			<div className="last-shipped-products">
				{products.length > 0 ? (
					<ul>
						{products.map((product) => {

							const adnGood = product.adnGood;
							const nstrGood = product.nstrGood;
							const idtfGood = product.idtfGood;

							return (
								<li key={product.id}>
									<div>
										<span>
											<ProductLink product={product} />
										</span>

										{adnGood && (
											<ProductTag
												type={ProductTypes.ADN}
												product={adnGood} />
										)}

										{nstrGood && (
											<ProductTag
												type={ProductTypes.NSTR}
												product={nstrGood} />
										)}

										{idtfGood && (
											<ProductTag
												type={ProductTypes.IDTF}
												product={idtfGood} />
										)}
									</div>
								</li>
							);
						})}
					</ul>
				) : (
					<div style={{ marginBottom: 8 }}>
						{Language("NONE")}
					</div>
				)}
			</div>

			{voyage && (
				<ProductLastShippedEditDialog
					open={dialogOpen}
					onClose={() => openDialog(false)}
					voyageId={voyage.id}
					products={products}
					refetch={refetch} />
			)}
		</FormGroup>
	);
};

type ProductForm = {
	product1?: Product | null;
	product2?: Product | null;
	product3?: Product | null;
};

type ProductLastShippedEditDialogProps = {
	open: boolean;
	onClose: () => void;
	voyageId: number;
	products?: Product[];
	refetch: () => void;
};

const ProductLastShippedEditDialog = ({ open, onClose, voyageId, products, refetch }: ProductLastShippedEditDialogProps) => {

	const confirm = useConfirm();

	const defaultValues = {
		product1: null,
		product2: null,
		product3: null,
	};

	const { control, handleSubmit, formState, reset } = useForm<ProductForm>({
		defaultValues: defaultValues,
	});

	const [ saveLastShippedProducts, { loading } ] = useMutation(LAST_SHIPPED_PRODUCTS_MUTATION);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const saveProducts = (data: ProductForm) => {
		const products = [ data.product1, data.product2, data.product3 ].filter(Boolean) as Product[];

		saveLastShippedProducts({
			variables: {
				voyageId: voyageId,
				productIds: [ ...products ].map(it => Number(it.id)),
			},
		}).then(() => {
			handleOnClose();
			refetch();
		});
	};

	const onSave = (data: ProductForm) => {
		if (!data.product1 || !data.product2 || !data.product3) {
			confirm({
				type: "WARNING",
				title: intl.formatMessage({ id: "LAST_SHIPPED_PRODUCTS_WARNING" }),
			}).then(() => {
				saveProducts(data);
			});
		} else {
			saveProducts(data);
		}
	};

	useEffect(() => {
		if (open) {
			reset({
				product1: products?.[0],
				product2: products?.[1],
				product3: products?.[2],
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "RECENTLY_SHIPPED_PRODUCTS" })}
			form='productLastShippedForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(onSave)(event)} id="productLastShippedForm">
				<FormGroup label={intl.formatMessage({ id: "PRODUCT" })} overflow="hidden">
					<FormField name="product1" errors={formState.errors}>
						<Controller
							name='product1'
							control={control}
							render={({ field }) => <ProductSelect {...field} />} />
					</FormField>
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "PRODUCT" })} overflow="hidden">
					<FormField name="product2" errors={formState.errors}>
						<Controller
							name='product2'
							control={control}
							render={({ field }) => <ProductSelect {...field} />} />
					</FormField>
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "PRODUCT" })} overflow="hidden">
					<FormField name="product3" errors={formState.errors}>
						<Controller
							name='product3'
							control={control}
							render={({ field }) => <ProductSelect {...field} />} />
					</FormField>
				</FormGroup>
			</form>
		</FormDialog>
	);
};

const LAST_SHIPPED_PRODUCTS_MUTATION = gql(`#graphql
mutation productLastShipped_changeLastShippedProduct($voyageId: Long!, $productIds: [Long]) {
	changeLastShippedProductsOnVoyage(voyageId: $voyageId, productIds: $productIds) {
		id
	}
}
`);

export default LastShippedProductsMasterData;
